import { ZONE_KIND_COLORS, ZONE_TYPES, type PlaceMatch, type ZoneType } from '@chukua/common';
import { Alert, Button, Form, Input, Radio, Select, Switch } from 'antd';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { ReactNode } from 'react';
import { ZoneKindSwatch } from '../../../../../shared/components/ZoneKindSwatch';
import type { ZoneKind } from '../../../../reference-data/reference-data.api';
import type { ZoneFields } from '../hooks/use-zone-panel';
import { codeFromName } from '../policies/zone-code';
import { countryName, ZONE_TYPE_LABELS } from '../../../policies/zone-labels';
import { ZonePlaceSearch } from './ZonePlaceSearch';
import type { Zone } from '../../../zones.api';

interface ZonePanelFormProps {
  /** The zone being edited, or null for a new one. */
  zone: Zone | null;
  zoneKinds: ZoneKind[];
  error: string | null;
  submitting: boolean;
  /** The shape controls, between the fields and the buttons. */
  shapeControls: ReactNode;
  onSave: (fields: ZoneFields) => Promise<void>;
  onCancel: () => void;
  /** For a new zone's place search: the map ranks places near its centre. */
  map: MapLibreMap | null;
  onPickPlace: (place: PlaceMatch) => void;
  onPickZone: (zoneId: string) => void;
}

/** Active kinds, plus the zone's own if it has since been deactivated, so the Select has something to display. */
function kindOptions(zoneKinds: ZoneKind[], zone: Zone | null) {
  const offered = zoneKinds.filter((kind) => kind.isActive || kind.code === zone?.kind);
  return offered.map((kind) => ({
    value: kind.code,
    label: (
      <span className="flex items-center gap-2">
        <ZoneKindSwatch color={kind.color} />
        {kind.name}
      </span>
    ),
    title: `${kind.name} (${ZONE_KIND_COLORS[kind.color].label})`,
  }));
}

function initialValues(zone: Zone | null, zoneKinds: ZoneKind[]): ZoneFields {
  if (!zone) return { code: '', name: '', kind: zoneKinds.find((kind) => kind.isActive)?.code ?? '', type: 'place', watchesCrossings: false };
  return { code: zone.code, name: zone.name, kind: zone.kind, type: zone.type, watchesCrossings: zone.watchesCrossings };
}

/** Chosen when the zone is made and fixed after: a used place turned boundary would orphan the rate lines and jobs that key on its code. */
function TypeField({ isExisting }: { isExisting: boolean }) {
  const extra = isExisting
    ? 'A zone cannot change type. Create a new one instead.'
    : 'Places are where jobs go. Boundaries (gate lanes, weighbridges, border posts) are only watched.';
  return (
    <Form.Item name="type" label="Type" extra={extra}>
      <Radio.Group disabled={isExisting} optionType="button" options={ZONE_TYPES.map((value) => ({ value, label: ZONE_TYPE_LABELS[value] }))} />
    </Form.Item>
  );
}

/** A place chooses whether to record arrivals and departures; a boundary always does. */
function WatchingField({ type }: { type: ZoneType | undefined }) {
  if (type === 'boundary') {
    return (
      <div className="mb-6 flex items-baseline justify-between gap-3 text-sm">
        <span className="text-fg">Arrivals and departures</span>
        <span className="text-right text-muted">Always watched</span>
      </div>
    );
  }
  return (
    <Form.Item name="watchesCrossings" label="Record arrivals and departures" valuePropName="checked" extra="Note when a truck enters or leaves this place.">
      <Switch />
    </Form.Item>
  );
}

/**
 * The zone's fields; its shape is drawn on the map and saved with them. A new zone starts from a place found
 * by name, which fills in the name and code, and its code follows the name until the admin types one.
 */
export function ZonePanelForm({ zone, zoneKinds, error, submitting, shapeControls, onSave, onCancel, map, onPickPlace, onPickZone }: ZonePanelFormProps) {
  const [form] = Form.useForm<ZoneFields>();
  const type = Form.useWatch('type', form);

  function suggestCode(changed: Partial<ZoneFields>) {
    // An existing zone's code is what rate lines key on: never rewrite it.
    if (zone || changed.name === undefined || form.isFieldTouched('code')) return;
    form.setFieldsValue({ code: codeFromName(changed.name) });
  }

  function pickPlace(place: PlaceMatch) {
    form.setFieldsValue({ name: place.name, ...(form.isFieldTouched('code') ? {} : { code: codeFromName(place.name) }) });
    onPickPlace(place);
  }

  return (
    <Form<ZoneFields>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={onSave}
      onValuesChange={suggestCode}
      disabled={submitting}
      initialValues={initialValues(zone, zoneKinds)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      {!zone && (
        <Form.Item label="Find a place" extra="Start from the business, site or town. You can adjust its area on the map after.">
          <ZonePlaceSearch map={map} onPickPlace={pickPlace} onPickZone={onPickZone} />
        </Form.Item>
      )}
      <TypeField isExisting={Boolean(zone)} />
      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="code" label="Code" extra="What rate lines and jobs key on." rules={[{ required: true, message: 'Enter a code' }]}>
        <Input className="font-mono" />
      </Form.Item>
      <Form.Item name="kind" label="Kind" rules={[{ required: true, message: 'Choose a kind' }]}>
        <Select options={kindOptions(zoneKinds, zone)} />
      </Form.Item>
      <WatchingField type={type} />
      <div className="mb-6 flex items-baseline justify-between gap-3 text-sm">
        <span className="text-fg">Country</span>
        <span className="text-right text-muted">{countryName(zone?.countryCode ?? null) ?? 'From the location, once saved'}</span>
      </div>
      <div className="mb-6">{shapeControls}</div>
      <div className="flex gap-2">
        <Button type="primary" htmlType="submit" loading={submitting}>
          {zone ? 'Save' : 'Create zone'}
        </Button>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </Form>
  );
}
